import type { PlatformEvent, WsClientMessage, WsServerMessage } from "../../shared/types";

type Handler = (event: PlatformEvent) => void;
export type ConnectionState = "connecting" | "open" | "closed";

/**
 * Single shared WebSocket. Tracks the last persisted seq per run for every topic so that
 * reconnects replay exactly what was missed.
 */
class Realtime {
  private ws: WebSocket | null = null;
  private handlers = new Map<string, Set<Handler>>();
  private lastSeq = new Map<string, Map<string, number>>();
  private retry = 0;
  /** Set once the app asks to stay connected, so an idle socket still reconnects. */
  private keepAlive = false;
  private stateListeners = new Set<(s: ConnectionState) => void>();
  state: ConnectionState = "closed";
  /** The build this page was loaded against: the first hello's. Another one later means a deploy has replaced it. */
  private build: string | null = null;
  private updateListeners = new Set<() => void>();
  updated = false;

  private setState(s: ConnectionState) {
    this.state = s;
    for (const fn of this.stateListeners) fn(s);
  }

  /** Keeps one socket open for as long as the app is open, so presence knows who is here. */
  ensureConnected() {
    this.keepAlive = true;
    this.connect();
  }

  onState(fn: (s: ConnectionState) => void): () => void {
    this.stateListeners.add(fn);
    fn(this.state);
    return () => this.stateListeners.delete(fn);
  }

  private connect() {
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return;
    this.setState("connecting");
    const ws = new WebSocket(`${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setState("open");
      for (const topic of this.handlers.keys()) this.sendSubscribe(topic);
    };
    ws.onmessage = (msg) => {
      const data = JSON.parse(msg.data) as WsServerMessage;
      if (data.op === "hello") return this.hello(data.build);
      if (data.op !== "event") return;
      const { topic, event } = data;
      if (event.seq > 0) {
        const seqs = this.lastSeq.get(topic) ?? new Map<string, number>();
        if ((seqs.get(event.runId) ?? 0) >= event.seq) return;
        seqs.set(event.runId, event.seq);
        this.lastSeq.set(topic, seqs);
      }
      for (const fn of this.handlers.get(topic) ?? []) fn(event);
    };
    ws.onclose = () => {
      this.setState("closed");
      this.ws = null;
      if (!this.keepAlive && this.handlers.size === 0) return;
      const delay = Math.min(1000 * 2 ** this.retry++, 15000);
      setTimeout(() => this.connect(), delay);
    };
  }

  private hello(build: string) {
    if (this.build === null) this.build = build;
    else if (build !== this.build && !this.updated) {
      this.updated = true;
      for (const fn of this.updateListeners) fn();
    }
  }

  /** Called once, when the platform this page talks to is no longer the build it was loaded from. */
  onUpdate(fn: () => void): () => void {
    this.updateListeners.add(fn);
    if (this.updated) fn();
    return () => this.updateListeners.delete(fn);
  }

  private send(msg: WsClientMessage) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private sendSubscribe(topic: string) {
    const since = Object.fromEntries(this.lastSeq.get(topic) ?? []);
    this.send({ op: "subscribe", topic, since });
  }

  /**
   * Subscribe to a topic. `since` seeds per-run seqs already loaded over REST so replay starts after them.
   * Passing an empty object for a conversation topic still triggers a replay of everything after seq 0.
   */
  subscribe(topic: string, handler: Handler, since?: Record<string, number>): () => void {
    if (since) {
      const seqs = this.lastSeq.get(topic) ?? new Map<string, number>();
      for (const [runId, seq] of Object.entries(since)) seqs.set(runId, Math.max(seqs.get(runId) ?? 0, seq));
      this.lastSeq.set(topic, seqs);
    }
    let set = this.handlers.get(topic);
    const isNew = !set;
    if (!set) this.handlers.set(topic, (set = new Set()));
    set.add(handler);
    this.connect();
    if (isNew) this.sendSubscribe(topic);
    return () => {
      set!.delete(handler);
      if (set!.size === 0) {
        this.handlers.delete(topic);
        this.lastSeq.delete(topic);
        this.send({ op: "unsubscribe", topic });
      }
    };
  }
}

export const realtime = new Realtime();
